/**
 * The SIM swap flow — the most dangerous of the client's three asks.
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ **The direction problem, and how this screen answers it**            │
 * │                                                                      │
 * │ The likeliest operator error here is swapping the wrong way round —  │
 * │ typing the ICCID of the SIM that should keep working into the field  │
 * │ that means "replace this". A screen showing "SIM A → SIM B" does not  │
 * │ prevent that, because both sides look alike and the arrow carries    │
 * │ the whole meaning.                                                    │
 * │                                                                      │
 * │ So this screen does not present two symmetric SIMs. It presents      │
 * │ **one number that moves**:                                            │
 * │                                                                      │
 * │   the mobile number stays  →  the card it lives on changes           │
 * │                                                                      │
 * │ The number is stated once, at the top, as the thing being moved. Then │
 * │ two asymmetric panels: the current SIM, labelled with what it loses   │
 * │ ("stops working immediately"), and the replacement, labelled with     │
 * │ what it gains ("takes over this number"). The consequence lives on    │
 * │ the panel it happens to, not in a sentence underneath both.          │
 * │                                                                      │
 * │ The current ICCID is **read-only and pre-filled from the SIM**, so    │
 * │ the operator cannot type it into the wrong field — there is only one  │
 * │ field to type into, and it is the one labelled "new".                 │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * Unlike activation, **friction is correct here**: it is irreversible, it is
 * not a daily bulk action, and a typed phrase is not devalued by overuse. So
 * `SIM_ACTIONS.swap.destructive` still drives the type-to-confirm gate.
 *
 * **`otp` is never sent** (§06a.6 tier 3). The vendor's field exists and is
 * optional, but nothing documents how an OTP is requested or delivered, so
 * there is no hidden branch and no "if required" input. `VendorCallLog` records
 * the vendor's error code, so the first real OTP rejection will name its own
 * trigger rather than being guessed at now.
 */

import type { SimSummary } from '@rd/shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { Badge } from '../../../components/Badge.js';
import { Button } from '../../../components/Button.js';
import { ErrorState } from '../../../components/ErrorState.js';
import { Field } from '../../../components/Field.js';
import { Input } from '../../../components/Input.js';
import { PageHeader } from '../../../components/PageHeader.js';
import { Skeleton } from '../../../components/Skeleton.js';
import { ApiClientError, apiRequest } from '../../../lib/apiClient.js';
import { useJobDrawer } from '../../../lib/JobDrawerContext.js';
import { SIM_ACTIONS } from '../actions/actionRegistry.js';
import { useSimAction } from '../actions/useSimAction.js';
import { simStatusLabel, simStatusTone } from '../statusTone.js';
import { refsNeedingReveal, useRevealedSims } from '../useRevealedSims.js';

const ACTION = SIM_ACTIONS.swap;

/** The vendor's ICCID shape: 18–20 digits. Checked before the wire. */
export function isPlausibleIccid(value: string): boolean {
  return /^\d{18,20}$/.test(value.trim());
}

export function SwapFlow(): ReactElement {
  const { simRef } = useParams<{ simRef: string }>();
  const navigate = useNavigate();
  const { track } = useJobDrawer();
  const { submit, isSubmitting, error } = useSimAction(ACTION);

  const [newIccid, setNewIccid] = useState('');
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');

  const sim = useQuery<SimSummary>({
    queryKey: ['sim', simRef],
    queryFn: () => apiRequest<SimSummary>(`/sims/${simRef ?? ''}`),
    enabled: Boolean(simRef),
  });

  // PR-8: the record above arrives masked (§B4), but this screen exists to
  // let the operator confirm the exact pair being posted — and the body's
  // `existingIccid` must be the real value or `iccidSchema` 422s it. The
  // revealed pair lives in this hook's state only, never the query cache.
  const reveal = useRevealedSims(refsNeedingReveal(sim.data ? [sim.data] : []));

  if (sim.isPending) return <Skeleton className="h-64 w-full" />;
  if (sim.isError || !sim.data) {
    return <ErrorState error={sim.error} onRetry={() => void sim.refetch()} />;
  }

  const current = sim.data;
  const revealedIds = reveal.revealed?.get(current.simRef);
  // Masked until the reveal lands — the mask-invariant digits keep the panels
  // recognisable while the full value is in flight.
  const shownMsisdn = revealedIds?.msisdn ?? current.msisdn;
  const shownIccid = revealedIds?.iccid ?? current.iccid;
  const trimmed = newIccid.trim();
  const sameAsCurrent = trimmed.length > 0 && trimmed === (shownIccid ?? '');
  const iccidValid = isPlausibleIccid(trimmed) && !sameAsCurrent;
  const confirmed = typed === ACTION.confirmWord;
  const canSubmit = iccidValid && confirmed && !isSubmitting;

  /*
    The server's own field errors, bound to the input they name — the pattern
    `BrandingPage` and the user forms already use.

    Without this the network's rejection landed in the page-level `ErrorState`
    as "Check the highlighted fields / Something in the form needs correcting",
    with no field highlighted and the actual sentence folded away under
    "Technical details". The one rule an operator has to learn here — that the
    replacement must be a spare — was the thing being hidden.
  */
  const serverFieldErrors = error instanceof ApiClientError ? error.fieldErrors() : {};

  const iccidError = sameAsCurrent
    ? 'That is the SIM already in service. Enter the number of the replacement SIM.'
    : trimmed.length > 0 && !isPlausibleIccid(trimmed)
      ? 'A SIM number is 18 to 20 digits.'
      : serverFieldErrors.newIccid;

  const run = async (): Promise<void> => {
    try {
      // Awaiting the shared promise (not reading state) means a confirm that
      // beats the reveal fetch waits for it instead of posting the mask.
      const revealedMap = await reveal.ensureRevealed();
      const job = await submit({
        existingIccid: revealedMap.get(current.simRef)?.iccid ?? current.iccid ?? '',
        newIccid: trimmed,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        // No `otp`. See the header — the field is deliberately never sent.
      });
      track(job.ref);
      void navigate(`/sims/${current.simRef}`);
    } catch {
      // `error` renders in place; the idempotency key survives so a retry of
      // the same payload cannot become a second swap.
    }
  };

  return (
    <>
      <PageHeader title="Swap SIM" description="Move this mobile number onto a replacement SIM." />

      <div className="flex flex-col gap-6 lg:max-w-3xl">
        {/* 1 · What moves. Stated once, as the subject of the whole page. */}
        <section className="rounded-[var(--radius-md)] border border-line bg-surface p-4">
          <p className="text-[length:var(--text-xs)] uppercase tracking-[0.04em] text-muted">
            The number being moved
          </p>
          <p
            className="mt-1 font-[family-name:var(--font-mono)] text-[length:var(--text-2xl)] font-semibold text-body"
            data-testid="swap-msisdn"
          >
            {shownMsisdn ?? '—'}
          </p>
          <p className="mt-1 text-[length:var(--text-sm)] text-muted">
            This number stays the same. The SIM card it works on is what changes.
          </p>
        </section>

        {/* 2 · From and to. Asymmetric on purpose: each panel is labelled with
            what happens TO IT, so the direction cannot be read backwards. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <section
            data-testid="swap-from"
            className="rounded-[var(--radius-md)] border border-line bg-surface p-4"
          >
            <div className="flex items-center justify-between gap-2">
              <p className="text-[length:var(--text-xs)] uppercase tracking-[0.04em] text-muted">
                Current SIM
              </p>
              <Badge tone={simStatusTone(current.status)}>{simStatusLabel(current.status)}</Badge>
            </div>
            <p className="mt-2 select-all break-all font-[family-name:var(--font-mono)] text-[length:var(--text-sm)] text-body">
              {shownIccid ?? '—'}
            </p>
            {/* The consequence sits on the panel it happens to. */}
            <p className="mt-2 text-[length:var(--text-sm)] font-medium text-danger-text">
              Stops working immediately, and cannot be used again.
            </p>
          </section>

          <section
            data-testid="swap-to"
            className="rounded-[var(--radius-md)] border border-line bg-surface p-4"
          >
            <p className="text-[length:var(--text-xs)] uppercase tracking-[0.04em] text-muted">
              Replacement SIM
            </p>
            <div className="mt-2">
              <Field
                label="New SIM number (ICCID)"
                /*
                  The rule, stated before the attempt rather than after it.

                  The network refuses a swap onto a SIM that is already in
                  service, and its refusal arrives as a generic rejection — so
                  every swap against the live account failed while the form
                  said only "the number printed on the replacement SIM", which
                  an operator holding an in-service SIM has no reason to doubt.
                */
                hint="The 18–20 digit number printed on the replacement SIM. It must be a spare — a SIM that is not already in service."
                error={iccidError}
                required
              >
                {(props) => (
                  <Input
                    {...props}
                    data-testid="swap-new-iccid"
                    value={newIccid}
                    inputMode="numeric"
                    autoComplete="off"
                    onChange={(e) => setNewIccid(e.target.value)}
                  />
                )}
              </Field>
            </div>
            <p className="mt-2 text-[length:var(--text-sm)] text-muted">
              Takes over {shownMsisdn ?? 'this number'} once the swap completes.
            </p>
          </section>
        </div>

        {/* 3 · Optional context for the audit trail. */}
        <section className="rounded-[var(--radius-md)] border border-line bg-surface p-4">
          <Field label="Reason (optional)" hint="Recorded against the job for your own records.">
            {(props) => (
              <Input {...props} value={reason} onChange={(e) => setReason(e.target.value)} />
            )}
          </Field>
        </section>

        {/* A failed reveal fetch surfaces here too — without it a 403 on
            `?reveal=true` would leave the submit silently doing nothing. */}
        {(error ?? reveal.error) ? <ErrorState error={error ?? reveal.error} /> : null}

        {/* 4 · The typed phrase, last. Friction is correct here: irreversible,
            occasional, and not devalued by daily use. */}
        <section className="flex flex-col gap-3">
          <p className="text-[length:var(--text-sm)] text-body">{ACTION.consequence}</p>

          <Field
            label={`Type ${ACTION.confirmWord} to confirm`}
            hint="This cannot be undone from this panel."
            required
          >
            {(props) => (
              <Input
                {...props}
                data-testid="confirm-input"
                value={typed}
                autoComplete="off"
                onChange={(e) => setTyped(e.target.value)}
                // Enter must not submit: the button is a separate act.
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.preventDefault();
                }}
              />
            )}
          </Field>

          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => void navigate(`/sims/${current.simRef}`)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={isSubmitting}
              disabled={!canSubmit}
              onClick={() => void run()}
            >
              Swap SIM
            </Button>
          </div>

          <Link
            to="/sims"
            className="text-[length:var(--text-sm)] text-primary underline-offset-2 hover:underline"
          >
            Back to the estate
          </Link>
        </section>
      </div>
    </>
  );
}
